import { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { Graph, type Edge, type Node } from "@antv/x6";
import { Button, Flex, Text } from "src/elements";
import { Color } from "src/elements/Color/Color";
import { useTranslation } from "src/i18n";
import { zIndexLevel } from "src/utils";
import type { SpineLevel } from "../domain/studioTypes";
import { poolingKeyOf } from "../domain/cdtLayout/buildFromCdt";
import type { PoolingStatus } from "../domain/cdtLayout/poolingQuestion";
import type { StudioTree, StudioTreeNode } from "../projections/treeProjection";
import {
  computeLadderLayout,
  TREE_COLUMN_GAP,
  TREE_NODE_WIDTH,
  type LadderLayoutNode,
} from "../projections/studioTreeLayout";
import {
  buildLadderView,
  canOpenLadderNode,
  RUNG_PREFIX,
  valuesKeyOf,
  type LadderNode,
} from "../projections/ladderView";
import { STUDIO_THEME } from "../constants/studioTheme";

interface Props {
  tree: StudioTree;
  spine: readonly SpineLevel[];
  selectedNodeId: string | null;
  readOnly: boolean;
  onSelectNode: (nodeId: string | null) => void;
  onMoveSibling: (node: StudioTreeNode, offset: number) => void;
  onMoveLevel: (from: number, to: number) => void;
  poolingStatus?: ReadonlyMap<string, PoolingStatus>;
  onLiftLevelHere?: (scopeNodeId: string, fromLevelIndex: number, toLevelIndex: number) => void;
}

const LEVEL_HEADER_PREFIX = "level-header:";
const LEVEL_HEADER_HEIGHT = 30;
const LEVEL_HEADER_Y = -(LEVEL_HEADER_HEIGHT + 14);
const COLUMN_PITCH = TREE_NODE_WIDTH + TREE_COLUMN_GAP;
const HEADER_BAND = Math.abs(LEVEL_HEADER_Y);

const TOGGLE_WIDTH = 26;
const TOGGLE_CENTRE_X = TOGGLE_WIDTH / 2;
const TOGGLE_BOX = 16;
const TOGGLE_SIGN = 8;

export const ZOOM_MIN = 0.1;
export const ZOOM_MAX = 2;
const ZOOM_STEP = 1.25;
const FIT_PADDING = 12;

export const clampZoom = (zoom: number): number => Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, zoom));

/**
 * Draw the graph by DIFFERENCE. Never clear and re-add.
 *
 * `clearCells()` only QUEUES each view's removal, and `addNodes` in the same tick
 * hands the same id to a freshly created view, which takes the id out of the
 * scheduler's live map before the queued removal can find it. The old view's SVG
 * group is then never taken out of the stage, so every redraw stacked another
 * complete copy of the tree behind the last one — a hundred deep after a minute,
 * showing as ghost slivers of a card under each card. Measured on flow 125: 10
 * cells in the model, 610 node groups in the DOM.
 *
 * Diffing also makes a redraw that changes nothing cost nothing: X6 compares each
 * prop it is handed, so an identical pass emits no change events at all.
 */
export const syncCells = (
  graph: Graph,
  nodes: readonly Node.Metadata[],
  edges: readonly Edge.Metadata[],
): void => {
  const wanted = new Map<string, { meta: Node.Metadata | Edge.Metadata; isEdge: boolean }>();
  for (const meta of nodes) wanted.set(String(meta.id), { meta, isEdge: false });
  for (const meta of edges) wanted.set(String(meta.id), { meta, isEdge: true });

  for (const cell of graph.getCells()) {
    const target = wanted.get(cell.id);
    // `prop` across node/edge shapes would corrupt the cell.
    if (!target || target.isEdge !== cell.isEdge()) cell.remove();
  }
  for (const [id, { meta, isEdge }] of wanted) {
    const existing = graph.getCellById(id);
    if (!existing) {
      if (isEdge) graph.addEdge(meta as Edge.Metadata);
      else graph.addNode(meta as Node.Metadata);
      continue;
    }
    const next = Object.fromEntries(
      Object.entries(meta as Record<string, unknown>).filter(([key]) => key !== "id"),
    );
    existing.prop(next);
  }
};

/**
 * The name of every column, derived from the tree rather than assumed: one entry per spine level, each a LIST, because branches under different parents can carry different layers at the same depth.
 * Read off the WHOLE projection (`tree.nodesById`), so the names stand whatever is expanded. The spine's own label is the fallback for a level no node reached.
 *
 * Every level is named on exactly ONE header. A `scopedLevelOrder` branch draws its rungs in
 * another level's column, and naming that column after them repeats a caption the level's own
 * header already carries — two headers reading "Pack type" is a duplicate level to the reader,
 * not a report. A label that is some OTHER spine level's name therefore stays with that level's
 * header; the column it visits keeps its own level's name. Labels that name no level (the
 * mixed-meaning column whose layers differ per parent) still all list.
 */
export const columnLabelsOf = (
  tree: StudioTree,
  spine: readonly SpineLevel[],
): readonly (readonly string[])[] => {
  const perDepth = spine.map(() => new Set<string>());
  for (const node of Object.values(tree.nodesById)) {
    perDepth[node.depth]?.add(node.levelLabel);
  }
  return spine.map((level, depth) => {
    const labels = [...(perDepth[depth] ?? [])].filter(
      (label) =>
        label === level.label ||
        !spine.some((other, otherDepth) => otherDepth !== depth && other.label === label),
    );
    return labels.length > 0 ? labels : [level.label];
  });
};

export const fitZoomOf = (
  content: { width: number; height: number },
  viewport: { width: number; height: number },
): number => {
  const width = viewport.width - FIT_PADDING * 2;
  const height = viewport.height - FIT_PADDING * 2;
  if (width <= 0 || height <= 0) return 1;
  return clampZoom(
    Math.min(width / Math.max(content.width, 1), height / Math.max(content.height, 1), 1),
  );
};

/**
 * The tree's own share of the panel, and — expanded — the whole viewport.
 * This layer sits INSIDE PlanogramPanel's stacking context, so L10 only has to clear the panel's own contents. Nothing between here and the panel carries a transform, so `inset: 0` resolves against the viewport.
 */
const TreeWorkspace = styled(Flex)<{ $isFullscreen: boolean }>`
  ${({ $isFullscreen }) =>
    $isFullscreen &&
    `
      position: fixed;
      inset: 0;
      z-index: ${zIndexLevel.L10};
      background: ${STUDIO_THEME.canvasBackground};
    `}
`;

const CanvasRoot = styled.div`
  position: relative;
  flex-grow: 1;
  min-height: 16rem;
`;

const GraphContainer = styled.div`
  position: absolute;
  inset: 0;
`;

/** The zoom lives ON the canvas, not in the actions row above it, where selecting a node pushes it off the panel's right edge. */
const ZoomBar = styled.div`
  position: absolute;
  top: 0.375rem;
  right: 0.375rem;
  z-index: 2;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.125rem 0.25rem;
  border: 1px solid ${STUDIO_THEME.border};
  border-radius: 0.375rem;
  background: ${STUDIO_THEME.surface};
`;

const FallbackList = styled.div<{ $isGraphReady: boolean }>`
  position: absolute;
  inset: 0;
  overflow: auto;
  padding: 0.5rem;
  background: ${STUDIO_THEME.canvasBackground};
  opacity: ${({ $isGraphReady }) => ($isGraphReady ? 0 : 1)};
  pointer-events: ${({ $isGraphReady }) => ($isGraphReady ? "none" : "auto")};
`;

/** The zoom for the DOM path, so the controls mean the same thing in both renders. Width is divided back out so rows still fill the viewport at any scale. */
const ScaledTree = styled.div<{ $zoom: number }>`
  transform: scale(${({ $zoom }) => $zoom});
  transform-origin: top left;
  width: ${({ $zoom }) => 100 / $zoom}%;
`;

const RowButton = styled.button<{ $selected: boolean; $depth: number }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  margin-left: ${({ $depth }) => $depth * 1}rem;
  padding: 0.25rem 0.5rem;
  border: 1px solid ${({ $selected }) => ($selected ? Color.selectedNodeBg : STUDIO_THEME.border)};
  border-radius: 0.25rem;
  background: ${({ $selected }) => ($selected ? Color.opacitySky : STUDIO_THEME.canvasBackground)};
  cursor: pointer;
  text-align: left;
`;

const InlineAction = styled.button`
  border: none;
  background: transparent;
  padding: 0 0.125rem;
  cursor: pointer;
  color: ${STUDIO_THEME.textSecondary};
  font-size: 0.6875rem;
  line-height: 1;

  &:hover {
    color: ${STUDIO_THEME.text};
  }
`;

const RungRow = styled.div<{ $depth: number }>`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  margin-left: ${({ $depth }) => $depth * 1}rem;
  padding: 0.125rem 0.5rem;
  border: 1px dashed ${STUDIO_THEME.textSecondary};
  border-radius: 0.25rem;
  background: ${STUDIO_THEME.surface};
`;

const ToggleBox = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1rem;
  height: 1rem;
  flex: none;
  padding: 0;
  border: 1px solid ${STUDIO_THEME.textSecondary};
  border-radius: 3px;
  background: ${Color.white};
  color: ${STUDIO_THEME.text};
  font-size: 0.75rem;
  font-weight: 800;
  line-height: 1;
  cursor: pointer;
`;

const Badge = styled.span<{ $background: string; $text: string }>`
  padding: 0 0.375rem;
  border-radius: 999px;
  background: ${({ $background }) => $background};
  color: ${({ $text }) => $text};
  font-size: 0.625rem;
  font-weight: 800;
`;

/** Drawn shapes: the app font renders ▸/▾ as empty boxes. */
const toggleMarkMarkup = (prefix: string) => [
  { tagName: "rect", selector: `${prefix}Box` },
  { tagName: "rect", selector: `${prefix}BarH` },
  { tagName: "rect", selector: `${prefix}BarV` },
];

const arrowMarkMarkup = (prefix: string) => [
  { tagName: "rect", selector: `${prefix}Box` },
  { tagName: "polygon", selector: `${prefix}Tri` },
];

const arrowMarkAttrs = (
  prefix: string,
  centreX: number,
  centreY: number,
  shown: boolean,
  open: boolean,
) => {
  const half = TOGGLE_SIGN / 2;
  const tri = open
    ? `${centreX - half},${centreY + half / 2} ${centreX + half},${centreY + half / 2} ${centreX},${centreY - half / 2}`
    : `${centreX - half},${centreY - half / 2} ${centreX + half},${centreY - half / 2} ${centreX},${centreY + half / 2}`;
  return {
    [`${prefix}Box`]: {
      x: centreX - TOGGLE_BOX / 2,
      y: centreY - TOGGLE_BOX / 2,
      width: shown ? TOGGLE_BOX : 0,
      height: shown ? TOGGLE_BOX : 0,
      rx: 3,
      ry: 3,
      fill: Color.white,
      stroke: STUDIO_THEME.textSecondary,
      strokeWidth: shown ? 1 : 0,
      cursor: "pointer",
    },
    [`${prefix}Tri`]: {
      points: shown ? tri : "",
      fill: STUDIO_THEME.text,
      pointerEvents: "none",
    },
  };
};

const toggleMarkAttrs = (
  prefix: string,
  centreX: number,
  centreY: number,
  shown: boolean,
  open: boolean,
) => ({
  [`${prefix}Box`]: {
    x: centreX - TOGGLE_BOX / 2,
    y: centreY - TOGGLE_BOX / 2,
    width: shown ? TOGGLE_BOX : 0,
    height: shown ? TOGGLE_BOX : 0,
    rx: 3,
    ry: 3,
    fill: Color.white,
    stroke: STUDIO_THEME.textSecondary,
    strokeWidth: shown ? 1 : 0,
    cursor: "pointer",
  },
  [`${prefix}BarH`]: {
    x: centreX - TOGGLE_SIGN / 2,
    y: centreY - 1,
    width: shown ? TOGGLE_SIGN : 0,
    height: 2,
    fill: STUDIO_THEME.text,
    pointerEvents: "none",
  },
  [`${prefix}BarV`]: {
    x: centreX - 1,
    y: centreY - TOGGLE_SIGN / 2,
    width: shown && !open ? 2 : 0,
    height: shown && !open ? TOGGLE_SIGN : 0,
    fill: STUDIO_THEME.text,
    pointerEvents: "none",
  },
});

const rungMetadataOf = (
  entry: LadderLayoutNode,
  selected: boolean,
  expanded: ReadonlySet<string>,
  label: string,
): Node.Metadata => {
  const { node, x, y, width, height } = entry;
  const midY = height / 2;
  const leftX = TOGGLE_WIDTH / 2;
  const rightX = width - TOGGLE_WIDTH / 2;
  const valuesOpen = expanded.has(valuesKeyOf(node.id));
  const chainOpen = expanded.has(node.id);
  return {
    id: node.id,
    x,
    y,
    width,
    height,
    markup: [
      { tagName: "rect", selector: "body" },
      { tagName: "rect", selector: "valuesHit" },
      ...arrowMarkMarkup("values"),
      { tagName: "rect", selector: "chainHit" },
      ...toggleMarkMarkup("chain"),
      { tagName: "text", selector: "label" },
    ],
    attrs: {
      body: {
        fill: selected ? Color.opacitySky : STUDIO_THEME.surface,
        stroke: selected ? Color.selectedNodeBorder : STUDIO_THEME.textSecondary,
        strokeWidth: 1,
        strokeDasharray: "3 2",
        rx: 4,
        ry: 4,
      },
      valuesHit: {
        x: 0,
        y: 0,
        width: node.canListValues ? TOGGLE_WIDTH : 0,
        height,
        fill: "transparent",
        cursor: "pointer",
      },
      ...arrowMarkAttrs("values", leftX, midY, node.canListValues, valuesOpen),
      chainHit: {
        x: width - TOGGLE_WIDTH,
        y: 0,
        width: node.canChain ? TOGGLE_WIDTH : 0,
        height,
        fill: "transparent",
        cursor: "pointer",
      },
      ...toggleMarkAttrs("chain", rightX, midY, node.canChain, chainOpen),
      label: {
        text: label,
        fill: STUDIO_THEME.textSecondary,
        fontSize: 10,
        fontWeight: 800,
        letterSpacing: 0.5,
        pointerEvents: "none",
      },
    },
  };
};

/** Where a dragged rung landed, as a column index. Read from the node's CENTRE, not its left edge; a deliberate sideways drag that still lands on its own column is taken at its word and moved one column that way. */
export const dropColumnOf = (
  droppedX: number,
  nodeWidth: number,
  originX: number,
  currentColumn: number,
  columnPitch: number,
): number => {
  const centre = droppedX + nodeWidth / 2;
  const landed = Math.max(0, Math.floor(centre / columnPitch));
  if (landed !== currentColumn) return landed;
  const travelled = droppedX - originX;
  if (Math.abs(travelled) < columnPitch * 0.3) return currentColumn;
  return currentColumn + (travelled < 0 ? -1 : 1);
};

export const boundsOf = (
  nodes: readonly LadderLayoutNode[],
): { minX: number; maxX: number; minY: number; maxY: number } | null => {
  if (nodes.length === 0) return null;
  return {
    minX: Math.min(...nodes.map((entry) => entry.x)),
    maxX: Math.max(...nodes.map((entry) => entry.x + entry.width)),
    minY: Math.min(...nodes.map((entry) => entry.y)),
    maxY: Math.max(...nodes.map((entry) => entry.y + entry.height)),
  };
};

const badgeTotal = (node: StudioTreeNode) =>
  node.flagCounts.blocking + node.flagCounts.toCheck + node.flagCounts.toReview;

const expandAllIdsOf = (tree: StudioTree): ReadonlySet<string> => {
  const expanded = new Set<string>();

  const visitScope = (scope: StudioTreeNode) => {
    if (scope.childIds.length === 0) return;
    expanded.add(scope.id);

    const firstLevel = scope.branchLevels[0] ?? scope.depth + 1;
    const nextLevels = scope.branchLevels.filter((levelIndex) => levelIndex > firstLevel);
    const firstRungId = `${RUNG_PREFIX}${firstLevel}:${scope.id}`;
    expanded.add(valuesKeyOf(firstRungId));
    for (const levelIndex of nextLevels) {
      expanded.add(`${RUNG_PREFIX}${levelIndex}:${scope.id}`);
    }

    scope.childIds.forEach((childId) => {
      const child = tree.nodesById[childId];
      if (child) visitScope(child);
    });
  };

  tree.rootIds.forEach((rootId) => {
    const root = tree.nodesById[rootId];
    if (root) visitScope(root);
  });
  return expanded;
};

export const StudioTreeCanvas = ({
  tree,
  spine,
  selectedNodeId,
  readOnly,
  onSelectNode,
  onMoveSibling,
  onMoveLevel,
  poolingStatus,
  onLiftLevelHere,
}: Props) => {
  const { t } = useTranslation("cdtStudio");
  const containerRef = useRef<HTMLDivElement | null>(null);
  const graphRef = useRef<Graph | null>(null);
  const [isGraphReady, setIsGraphReady] = useState(false);
  // Escape is only listened for while expanded, so the handler cannot outlive the state it closes.
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    if (!isFullscreen) return undefined;
    const collapseOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setIsFullscreen(false);
    };
    document.addEventListener("keydown", collapseOnEscape);
    return () => document.removeEventListener("keydown", collapseOnEscape);
  }, [isFullscreen]);
  /**
   * NOTHING IS EXPANDED UNTIL THE USER EXPANDS IT: the tree opens on the top row alone, and every level's name stands in the header band so the columns are answered without expanding anything.
   * The map holds only what the user has DECIDED, so a node appearing after a reorder or level move starts closed instead of inheriting a stale branch's state.
   */
  const [expansionChoices, setExpansionChoices] = useState<ReadonlyMap<string, boolean>>(
    () => new Map(),
  );
  const expandedIds = useMemo(() => {
    const next = new Set<string>();
    for (const [id, open] of expansionChoices) if (open) next.add(id);
    return next;
  }, [expansionChoices]);
  const columnLabels = useMemo(() => columnLabelsOf(tree, spine), [tree, spine]);
  const columnNames = useMemo(
    () => columnLabels.map((labels) => labels.join(", ")),
    [columnLabels],
  );
  const view = useMemo(() => buildLadderView(tree, spine, expandedIds), [tree, spine, expandedIds]);
  const layout = useMemo(() => computeLadderLayout(view), [view]);
  const contentColumns = useMemo(
    () => layout.nodes.reduce((most, entry) => Math.max(most, entry.node.column), 0) + 1,
    [layout],
  );
  const columnBandWidth = Math.max(contentColumns * COLUMN_PITCH - TREE_COLUMN_GAP, 0);
  const columnBandWidthRef = useRef(columnBandWidth);
  columnBandWidthRef.current = columnBandWidth;
  const [zoom, setZoom] = useState(1);
  const [autoFit, setAutoFit] = useState(true);
  const zoomRef = useRef(zoom);
  const autoFitRef = useRef(autoFit);
  autoFitRef.current = autoFit;
  const contentKeyRef = useRef("");
  const drawnIdsRef = useRef<ReadonlySet<string>>(new Set());
  const layoutRef = useRef(layout);
  layoutRef.current = layout;
  const onSelectRef = useRef(onSelectNode);
  onSelectRef.current = onSelectNode;
  const onMoveSiblingRef = useRef(onMoveSibling);
  onMoveSiblingRef.current = onMoveSibling;
  const onMoveLevelRef = useRef(onMoveLevel);
  onMoveLevelRef.current = onMoveLevel;
  const treeRef = useRef(tree);
  treeRef.current = tree;
  const spineRef = useRef(spine);
  spineRef.current = spine;
  const readOnlyRef = useRef(readOnly);
  readOnlyRef.current = readOnly;

  const toggleExpanded = (nodeId: string) => {
    const open = !expandedIds.has(nodeId);
    setExpansionChoices((current) => new Map(current).set(nodeId, open));
  };
  const expandAll = () => {
    const allExpanded = expandAllIdsOf(treeRef.current);
    setExpansionChoices(new Map([...allExpanded].map((id) => [id, true] as const)));
  };
  const collapseAll = () => {
    setExpansionChoices(new Map());
  };
  const toggleExpandedRef = useRef(toggleExpanded);
  toggleExpandedRef.current = toggleExpanded;
  const viewRef = useRef(view);
  viewRef.current = view;
  const selectedFocusRef = useRef<string | null>(selectedNodeId);
  selectedFocusRef.current = selectedNodeId;
  /** The rung the user is pointing at, kept local to the canvas: a rung is not a hierarchy member, so the panel's SKU-highlighting selection must never hold one. */
  const [selectedRungId, setSelectedRungId] = useState<string | null>(null);
  const onSelectRungRef = useRef((rungId: string) => setSelectedRungId(rungId));
  onSelectRungRef.current = (rungId: string) => setSelectedRungId(rungId);
  const onLiftLevelRef = useRef(onLiftLevelHere);
  onLiftLevelRef.current = onLiftLevelHere;

  const contentSizeOf = () => ({
    width: Math.max(layoutRef.current.width, columnBandWidthRef.current),
    height: layoutRef.current.height + HEADER_BAND,
  });
  const viewportSizeOf = () => ({
    width: containerRef.current?.clientWidth ?? 0,
    height: containerRef.current?.clientHeight ?? 0,
  });

  /** The point a zoom keeps still: the selected node if there is one, else the content's own bounding box — never the canvas centre, which is mostly empty. */
  const zoomCentreOf = (): { x: number; y: number } | undefined => {
    const nodes = layoutRef.current.nodes;
    if (nodes.length === 0) return undefined;
    const focus = nodes.find((entry) => entry.node.id === selectedFocusRef.current);
    if (focus) return { x: focus.x + focus.width / 2, y: focus.y + focus.height / 2 };
    const minX = Math.min(...nodes.map((entry) => entry.x));
    const maxX = Math.max(...nodes.map((entry) => entry.x + entry.width));
    const minY = Math.min(...nodes.map((entry) => entry.y));
    const maxY = Math.max(...nodes.map((entry) => entry.y + entry.height));
    return { x: (minX + maxX) / 2, y: (minY + maxY) / 2 };
  };

  const applyZoom = (next: number, aroundContent = false) => {
    const clamped = clampZoom(next);
    zoomRef.current = clamped;
    setZoom(clamped);
    if (aroundContent) {
      try {
        graphRef.current?.zoomTo(clamped, { center: zoomCentreOf() });
        return;
      } catch {
        // Fall through to the plain zoom below.
      }
    }
    try {
      graphRef.current?.zoomTo(clamped);
    } catch {
      // The graph may be mid-teardown; the DOM scale still applies.
    }
  };

  const fitToViewport = () => {
    applyZoom(fitZoomOf(contentSizeOf(), viewportSizeOf()));
    try {
      graphRef.current?.positionPoint({ x: 0, y: LEVEL_HEADER_Y }, FIT_PADDING, FIT_PADDING);
    } catch {
      // Best effort: nothing to position before the graph starts.
    }
  };
  const fitToViewportRef = useRef(fitToViewport);
  fitToViewportRef.current = fitToViewport;

  const stepZoom = (factor: number) => {
    setAutoFit(false);
    applyZoom(zoomRef.current * factor, true);
  };
  const resetZoom = () => {
    setAutoFit(true);
    fitToViewport();
  };

  useEffect(() => {
    if (!containerRef.current) return undefined;
    let observer: ResizeObserver | null = null;
    try {
      const graph = new Graph({
        container: containerRef.current,
        autoResize: true,
        panning: true,
        // Ctrl/⌘ + wheel zooms; a bare wheel is left to the page so the tree does not rescale when someone scrolls the panel.
        mousewheel: {
          enabled: true,
          modifiers: ["ctrl", "meta"],
          minScale: ZOOM_MIN,
          maxScale: ZOOM_MAX,
        },
        background: { color: STUDIO_THEME.canvasBackground },
        connecting: {
          router: "orth",
          connector: { name: "rounded" },
          sourceAnchor: "right",
          targetAnchor: "left",
        },
        interacting: () => ({ nodeMovable: !readOnlyRef.current }),
      });
      graphRef.current = graph;

      // Selecting redraws every cell before a second click arrives, so expand is on single-click.
      graph.on("node:click", ({ node, x }) => {
        if (node.id.startsWith(LEVEL_HEADER_PREFIX)) return;
        const entry = viewRef.current.nodesById[node.id];
        if (!entry) return;
        const local = x - node.position().x;
        if (entry.kind === "value") {
          if (local <= TOGGLE_WIDTH && canOpenLadderNode(entry)) {
            toggleExpandedRef.current(entry.id);
            return;
          }
          onSelectRef.current(entry.id);
          return;
        }
        if (local <= TOGGLE_WIDTH && entry.canListValues) {
          toggleExpandedRef.current(valuesKeyOf(entry.id));
          return;
        }
        if (local >= TREE_NODE_WIDTH - TOGGLE_WIDTH && entry.canChain) {
          toggleExpandedRef.current(entry.id);
          return;
        }
        onSelectRungRef.current(entry.id);
      });
      graph.on("node:dblclick", ({ node }) => {
        if (node.id.startsWith(LEVEL_HEADER_PREFIX)) return;
        const entry = viewRef.current.nodesById[node.id];
        if (entry?.kind === "value") toggleExpandedRef.current(entry.id);
      });
      // Ctrl+wheel changes the scale behind our back: adopt it and stand auto-fit down, so the next expand does not throw the user's zoom away.
      graph.on("scale", ({ sx }: { sx: number }) => {
        if (Math.abs(sx - zoomRef.current) < 0.001) return;
        zoomRef.current = sx;
        setZoom(sx);
        setAutoFit(false);
      });
      graph.on("node:mouseup", ({ node }) => {
        if (readOnlyRef.current) return;
        if (node.id.startsWith(LEVEL_HEADER_PREFIX)) {
          const from = Number(node.id.slice(LEVEL_HEADER_PREFIX.length));
          const bbox = node.getBBox();
          try {
            node.setPosition(from * COLUMN_PITCH, LEVEL_HEADER_Y);
          } catch {
            // Best effort: the graph may reject the restore during teardown.
          }
          if (Math.abs(bbox.x - from * COLUMN_PITCH) < 8) return;
          const to = Math.max(
            0,
            Math.min(spineRef.current.length - 1, Math.round(bbox.x / COLUMN_PITCH)),
          );
          if (to !== from) onMoveLevelRef.current(from, to);
          return;
        }
        const current = layoutRef.current;
        const dragged = current.nodes.find((candidate) => candidate.node.id === node.id);
        if (!dragged) return;
        const bbox = node.getBBox();
        if (dragged.node.kind === "level") {
          try {
            node.setPosition(dragged.x, dragged.y);
          } catch {
            // Best effort: the graph may reject the restore during teardown.
          }
          const scope = dragged.node.scopeId;
          // The floor is the branch's own first level: a rung cannot be lifted above the node it belongs to (the same bound swapLevelsUnderScope enforces).
          const floor = (treeRef.current.nodesById[scope]?.depth ?? -1) + 1;
          const target = Math.max(
            floor,
            Math.min(
              spineRef.current.length - 1,
              dropColumnOf(bbox.x, dragged.width, dragged.x, dragged.node.levelIndex, COLUMN_PITCH),
            ),
          );
          if (target !== dragged.node.levelIndex) {
            onLiftLevelRef.current?.(scope, dragged.node.levelIndex, target);
          }
          return;
        }
        const restore = () => {
          try {
            node.setPosition(dragged.x, dragged.y);
          } catch {
            // Best effort: the graph may reject the restore during teardown.
          }
        };
        if (Math.hypot(bbox.x - dragged.x, bbox.y - dragged.y) < 8) return;
        const centerY = bbox.y + bbox.height / 2;
        const draggedTree = dragged.node.tree;
        if (!draggedTree) {
          restore();
          return;
        }
        // Siblings are VALUES sharing a parent; the rung between them is not a sibling.
        const siblings = current.nodes.filter(
          (candidate) =>
            candidate.node.kind === "value" &&
            candidate.node.tree?.parentId === draggedTree.parentId,
        );
        let target: (typeof current.nodes)[number] | null = null;
        let best = Infinity;
        for (const candidate of siblings) {
          if (candidate.node.id === dragged.node.id) continue;
          const distance = Math.abs(centerY - (candidate.y + candidate.height / 2));
          if (distance < best) {
            best = distance;
            target = candidate;
          }
        }
        if (target) {
          const fromIndex = siblings.findIndex((s) => s.node.id === dragged.node.id);
          const toIndex = siblings.findIndex((s) => s.node.id === target?.node.id);
          onMoveSiblingRef.current(draggedTree, toIndex - fromIndex);
        }
        restore();
      });
      setIsGraphReady(true);
      if (typeof ResizeObserver !== "undefined") {
        observer = new ResizeObserver(() => {
          if (autoFitRef.current) fitToViewportRef.current();
        });
        observer.observe(containerRef.current);
      }
    } catch {
      setIsGraphReady(false);
    }
    return () => {
      observer?.disconnect();
      try {
        graphRef.current?.dispose();
      } catch {
        // Disposal is best effort during teardown.
      }
      graphRef.current = null;
    };
  }, []);

  useEffect(() => {
    const graph = graphRef.current;
    if (!graph) return;
    try {
      const cardCells: Node.Metadata[] = layout.nodes.map((entry): Node.Metadata => {
        const { x, y, width, height } = entry;
        if (entry.node.kind === "level") {
          return rungMetadataOf(
            entry,
            selectedRungId === entry.node.id,
            expandedIds,
            entry.node.levelLabel.toUpperCase(),
          );
        }
        const node = entry.node.tree as StudioTreeNode;
        const selected = selectedNodeId === node.id;
        const flagged = badgeTotal(node);
        const hasChildren = node.childIds.length > 0;
        const open = expandedIds.has(node.id);
        return {
          id: node.id,
          x,
          y,
          width,
          height,
          markup: [
            { tagName: "rect", selector: "body" },
            { tagName: "rect", selector: "toggleHit" },
            { tagName: "rect", selector: "toggleBox" },
            { tagName: "rect", selector: "toggleBarH" },
            { tagName: "rect", selector: "toggleBarV" },
            { tagName: "text", selector: "label" },
          ],
          attrs: {
            toggleHit: {
              x: 0,
              y: 0,
              width: TOGGLE_WIDTH,
              height,
              fill: "transparent",
              cursor: hasChildren ? "pointer" : "default",
            },
            toggleBox: {
              x: TOGGLE_CENTRE_X - TOGGLE_BOX / 2,
              y: height / 2 - TOGGLE_BOX / 2,
              width: TOGGLE_BOX,
              height: TOGGLE_BOX,
              rx: 3,
              ry: 3,
              fill: hasChildren ? Color.white : "transparent",
              stroke: hasChildren ? STUDIO_THEME.textSecondary : "transparent",
              strokeWidth: 1,
              cursor: hasChildren ? "pointer" : "default",
            },
            toggleBarH: {
              x: TOGGLE_CENTRE_X - TOGGLE_SIGN / 2,
              y: height / 2 - 1,
              width: hasChildren ? TOGGLE_SIGN : 0,
              height: 2,
              fill: STUDIO_THEME.text,
              pointerEvents: "none",
            },
            toggleBarV: {
              x: TOGGLE_CENTRE_X - 1,
              y: height / 2 - TOGGLE_SIGN / 2,
              width: hasChildren && !open ? 2 : 0,
              height: hasChildren && !open ? TOGGLE_SIGN : 0,
              fill: STUDIO_THEME.text,
              pointerEvents: "none",
            },
            body: {
              fill: selected ? Color.selectedNodeBg : STUDIO_THEME.canvasBackground,
              stroke: selected
                ? Color.selectedNodeBorder
                : flagged > 0
                  ? STUDIO_THEME.severity[
                      node.flagCounts.blocking > 0
                        ? "blocking"
                        : node.flagCounts.toCheck > 0
                          ? "toCheck"
                          : "toReview"
                    ].icon
                  : STUDIO_THEME.border,
              strokeWidth: flagged > 0 ? 2 : 1,
              rx: 8,
              ry: 8,
            },
            label: {
              text: `${node.value}\n${t("hierarchy.nodeMeta", {
                count: node.itemCount,
                flags: flagged,
              })}`,
              fill: selected ? Color.white : STUDIO_THEME.text,
              fontSize: 11,
              refX: 0.5 + TOGGLE_WIDTH / 2 / TREE_NODE_WIDTH,
              pointerEvents: "none",
            },
          },
        };
      });
      const edgeCells: Edge.Metadata[] = layout.edges.map(
        ({ id, source, target, orientation }) => ({
          id,
          source:
            orientation === "down"
              ? { cell: source, anchor: { name: "bottom" } }
              : { cell: source },
          target:
            orientation === "down" ? { cell: target, anchor: { name: "top" } } : { cell: target },
          attrs: { line: { stroke: STUDIO_THEME.border, strokeWidth: 1, targetMarker: null } },
        }),
      );
      const headerCells: Node.Metadata[] = columnNames.map((name, depth) => ({
        id: `${LEVEL_HEADER_PREFIX}${depth}`,
        x: depth * COLUMN_PITCH,
        y: LEVEL_HEADER_Y,
        width: TREE_NODE_WIDTH,
        height: LEVEL_HEADER_HEIGHT,
        attrs: {
          body: {
            fill: STUDIO_THEME.surface,
            stroke: STUDIO_THEME.border,
            strokeWidth: 1,
            rx: 6,
            ry: 6,
          },
          label: {
            text: readOnly ? name : `⇄ ${name}`,
            fill: STUDIO_THEME.textSecondary,
            fontSize: 11,
            fontWeight: 700,
            textWrap: { width: -10, height: LEVEL_HEADER_HEIGHT - 6, ellipsis: true },
          },
        },
      }));
      syncCells(graph, [...cardCells, ...headerCells], edgeCells);
      const contentKey = [...columnNames, ...layout.nodes.map((entry) => entry.node.id)].join("|");
      if (contentKey !== contentKeyRef.current) {
        contentKeyRef.current = contentKey;
        const revealed = layout.nodes.filter((entry) => !drawnIdsRef.current.has(entry.node.id));
        drawnIdsRef.current = new Set(layout.nodes.map((entry) => entry.node.id));
        if (autoFitRef.current) {
          fitToViewportRef.current();
        } else {
          graph.zoomTo(zoomRef.current);
          const box = boundsOf(revealed);
          const area = graph.getGraphArea();
          const outside =
            box !== null &&
            (box.minX < area.x ||
              box.maxX > area.x + area.width ||
              box.minY < area.y ||
              box.maxY > area.y + area.height);
          if (outside) {
            graph.centerPoint((box.minX + box.maxX) / 2, (box.minY + box.maxY) / 2);
          }
        }
      }
    } catch {
      setIsGraphReady(false);
    }
  }, [layout, expandedIds, columnNames, selectedNodeId, spine, readOnly, t]);

  const renderFallbackRung = (entry: LadderNode): React.ReactNode => {
    const valuesOpen = expandedIds.has(valuesKeyOf(entry.id));
    const chainOpen = expandedIds.has(entry.id);
    const rungScope = tree.nodesById[entry.scopeId];
    const above =
      (rungScope?.branchSpine ?? spine)[entry.levelIndex - 1]?.label ??
      spine[entry.levelIndex - 1]?.label ??
      "";
    return (
      <Flex key={entry.id} column gap="2px">
        <RungRow $depth={entry.column} data-testid={`cdt-studio-tree-rung-${entry.id}`}>
          {entry.canListValues && (
            <ToggleBox
              type="button"
              aria-label={t(valuesOpen ? "hierarchy.hideValues" : "hierarchy.showValues", {
                label: entry.levelLabel,
              })}
              onClick={() => toggleExpanded(valuesKeyOf(entry.id))}
            >
              {valuesOpen ? "▴" : "▾"}
            </ToggleBox>
          )}
          <Text variant="small2" color={STUDIO_THEME.textSecondary} bold>
            {entry.levelLabel.toUpperCase()}
          </Text>
          {entry.canChain && (
            <ToggleBox
              type="button"
              aria-label={t(chainOpen ? "hierarchy.hideNextLevel" : "hierarchy.showNextLevel", {
                label: entry.levelLabel,
              })}
              onClick={() => toggleExpanded(entry.id)}
            >
              {chainOpen ? "-" : "+"}
            </ToggleBox>
          )}
          {/* The branch-local lift as a button as well as a drag: at the scales a six-level tree fits at, a drag target is only a few pixels wide. */}
          {!readOnly && entry.canLift && onLiftLevelHere && (
            <InlineAction
              as="span"
              role="button"
              aria-label={t("hierarchy.liftLevelHere", { label: entry.levelLabel, above })}
              onClick={() => onLiftLevelHere(entry.scopeId, entry.levelIndex, entry.levelIndex - 1)}
            >
              {t("hierarchy.liftLevelShort")}
            </InlineAction>
          )}
        </RungRow>
        {entry.childIds.map((childId) => renderFallbackNode(childId))}
      </Flex>
    );
  };

  const renderFallbackNode = (viewId: string): React.ReactNode => {
    const entry = view.nodesById[viewId];
    if (!entry) return null;
    if (entry.kind === "level") return renderFallbackRung(entry);
    const node = entry.tree as StudioTreeNode;
    const nodeId = node.id;
    const expanded = expandedIds.has(nodeId);
    const flagged = badgeTotal(node);
    return (
      <Flex key={nodeId} column gap="2px">
        <RowButton
          type="button"
          $selected={selectedNodeId === nodeId}
          $depth={node.depth}
          data-testid={`cdt-studio-tree-node-${nodeId}`}
          onClick={() => onSelectNode(selectedNodeId === nodeId ? null : nodeId)}
        >
          {node.childIds.length > 0 && (
            <ToggleBox
              as="span"
              role="button"
              aria-label={t(expanded ? "hierarchy.collapseBranch" : "hierarchy.expandBranch", {
                label: node.value,
              })}
              onClick={(event) => {
                event.stopPropagation();
                toggleExpanded(nodeId);
              }}
            >
              {/* ASCII in a box that reads as a button: ▸/▾ render as empty squares here. */}
              {expanded ? "-" : "+"}
            </ToggleBox>
          )}
          <Text variant="small1" color={STUDIO_THEME.text} bold>
            {node.value}
          </Text>
          <Text variant="small2" color={STUDIO_THEME.textSecondary}>
            {t("hierarchy.nodeMeta", { count: node.itemCount, flags: flagged })}
          </Text>
          {flagged > 0 && (
            <Badge
              $background={STUDIO_THEME.severity.blocking.background}
              $text={STUDIO_THEME.severity.blocking.text}
            >
              {flagged}
            </Badge>
          )}
          {!readOnly && (
            <>
              <InlineAction
                as="span"
                role="button"
                aria-label={t("hierarchy.moveEarlier", { label: node.value })}
                onClick={(event) => {
                  event.stopPropagation();
                  onMoveSibling(node, -1);
                }}
              >
                ▲
              </InlineAction>
              <InlineAction
                as="span"
                role="button"
                aria-label={t("hierarchy.moveLater", { label: node.value })}
                onClick={(event) => {
                  event.stopPropagation();
                  onMoveSibling(node, 1);
                }}
              >
                ▼
              </InlineAction>
            </>
          )}
        </RowButton>
        {entry.childIds.map((childId) => renderFallbackNode(childId))}
      </Flex>
    );
  };

  const selectedRung =
    selectedRungId && view.nodesById[selectedRungId]?.kind === "level"
      ? view.nodesById[selectedRungId]
      : null;
  const selectedNode = selectedNodeId ? tree.nodesById[selectedNodeId] : null;
  const siblings = selectedNode
    ? selectedNode.parentId
      ? (tree.nodesById[selectedNode.parentId]?.childIds ?? [])
      : tree.rootIds
    : [];
  const siblingIndex = selectedNode ? siblings.indexOf(selectedNode.id) : -1;
  const selectedPooling = selectedNode
    ? poolingStatus?.get(poolingKeyOf(selectedNode.attr, selectedNode.value))
    : undefined;

  return (
    <TreeWorkspace
      column
      flexGrow
      minHeight="0"
      $isFullscreen={isFullscreen}
      data-testid="cdt-studio-tree-workspace"
    >
      <Flex
        align="center"
        gap="8px"
        padding="6px 12px"
        borderBottom={`1px solid ${STUDIO_THEME.border}`}
        background={STUDIO_THEME.surface}
        minHeight="2.25rem"
        // A selected node fills this line past the panel's width; wrapping keeps the trailing controls reachable.
        style={{ flexWrap: "wrap" }}
        data-testid="cdt-studio-tree-actions"
      >
        {selectedRung ? (
          <>
            <Text variant="small1" color={STUDIO_THEME.text} bold>
              {selectedRung.levelLabel.toUpperCase()}
            </Text>
            <Text variant="small2" color={STUDIO_THEME.textSecondary}>
              {t("hierarchy.rungScope", {
                scope: tree.nodesById[selectedRung.scopeId]?.value ?? t("hierarchy.wholePlan"),
              })}
            </Text>
            <Flex flexGrow />
            {!readOnly && selectedRung.canLift && onLiftLevelHere && (
              <Button
                size="small"
                variant="borderOnly"
                name="cdt-studio-lift-level"
                title={t("hierarchy.liftLevelTitle", {
                  label: selectedRung.levelLabel,
                  above:
                    (tree.nodesById[selectedRung.scopeId]?.branchSpine ?? spine)[
                      selectedRung.levelIndex - 1
                    ]?.label ??
                    spine[selectedRung.levelIndex - 1]?.label ??
                    "",
                })}
                onClick={() =>
                  onLiftLevelHere(
                    selectedRung.scopeId,
                    selectedRung.levelIndex,
                    selectedRung.levelIndex - 1,
                  )
                }
              >
                {t("hierarchy.liftLevelAbove", {
                  above:
                    (tree.nodesById[selectedRung.scopeId]?.branchSpine ?? spine)[
                      selectedRung.levelIndex - 1
                    ]?.label ??
                    spine[selectedRung.levelIndex - 1]?.label ??
                    "",
                })}
              </Button>
            )}
            <Button
              size="small"
              variant="borderless"
              name="cdt-studio-rung-done"
              onClick={() => setSelectedRungId(null)}
            >
              {t("hierarchy.rungDone")}
            </Button>
          </>
        ) : selectedNode ? (
          <>
            <Text variant="small1" color={STUDIO_THEME.text} bold>
              {selectedNode.value}
            </Text>
            <Text variant="small2" color={STUDIO_THEME.textSecondary}>
              {t("hierarchy.siblingPosition", {
                position: siblingIndex + 1,
                total: siblings.length,
              })}
            </Text>
            <Flex flexGrow />
            {!readOnly && (
              <>
                <Button
                  size="small"
                  variant="borderOnly"
                  isDisabled={siblingIndex <= 0}
                  onClick={() => onMoveSibling(selectedNode, -1)}
                >
                  {t("hierarchy.moveEarlierShort")}
                </Button>
                <Button
                  size="small"
                  variant="borderOnly"
                  isDisabled={siblingIndex < 0 || siblingIndex >= siblings.length - 1}
                  onClick={() => onMoveSibling(selectedNode, 1)}
                >
                  {t("hierarchy.moveLaterShort")}
                </Button>
                {/* Chip-strip level lifts withdrawn: the canvas ladder is the only control. */}
              </>
            )}
            {selectedPooling && (
              <Text variant="small2" color={STUDIO_THEME.textSecondary}>
                {t(
                  selectedPooling.built === "vertical"
                    ? "hierarchy.blockedVertical"
                    : "hierarchy.blockedHorizontal",
                )}
                {selectedPooling.ambiguous &&
                  ` · ${t("hierarchy.poolingAmbiguous", {
                    ratio: selectedPooling.facingsPerShelf,
                  })}`}
              </Text>
            )}
            {/* The imposed swap-to-vertical/horizontal button lived here.
             * Retired 2026-08-31 by owner ruling: "it did something
             * extremely weird. i also can not hit undo... for now just
             * remove the make X a vertical block". The pooling ANSWER chips
             * (the studio's own question) are untouched — they answer, this
             * imposed. */}
            {selectedNode.childIds.length > 0 && (
              <Button
                size="small"
                variant="borderless"
                onClick={() => toggleExpanded(selectedNode.id)}
              >
                {expandedIds.has(selectedNode.id)
                  ? t("hierarchy.collapseNode")
                  : t("hierarchy.expandNode")}
              </Button>
            )}
          </>
        ) : (
          <Flex flexGrow />
        )}
      </Flex>
      <CanvasRoot data-testid="cdt-studio-tree">
        <GraphContainer ref={containerRef} />
        <ZoomBar data-testid="cdt-studio-tree-zoom">
          <Button
            size="small"
            variant="borderOnly"
            name="cdt-studio-zoom-out"
            ariaLabel={t("hierarchy.zoomOut")}
            title={t("hierarchy.zoomOut")}
            isDisabled={zoom <= ZOOM_MIN + 0.001}
            onClick={() => stepZoom(1 / ZOOM_STEP)}
          >
            −
          </Button>
          <Text variant="small2" color={STUDIO_THEME.textSecondary}>
            {t("hierarchy.zoomLevel", { percent: Math.round(zoom * 100) })}
          </Text>
          <Button
            size="small"
            variant="borderOnly"
            name="cdt-studio-zoom-in"
            ariaLabel={t("hierarchy.zoomIn")}
            title={t("hierarchy.zoomIn")}
            isDisabled={zoom >= ZOOM_MAX - 0.001}
            onClick={() => stepZoom(ZOOM_STEP)}
          >
            +
          </Button>
          <Button
            size="small"
            variant="borderless"
            name="cdt-studio-zoom-fit"
            title={t("hierarchy.zoomFitTitle")}
            onClick={resetZoom}
          >
            {t("hierarchy.zoomFit")}
          </Button>
          <Button
            size="small"
            variant="borderless"
            name="cdt-studio-expand-all-nodes"
            title={t("hierarchy.expandAllNodes")}
            onClick={expandAll}
          >
            {t("hierarchy.expandAllNodes")}
          </Button>
          <Button
            size="small"
            variant="borderless"
            name="cdt-studio-collapse-all-nodes"
            title={t("hierarchy.collapseAllNodes")}
            onClick={collapseAll}
          >
            {t("hierarchy.collapseAllNodes")}
          </Button>
          <Button
            size="small"
            variant="borderOnly"
            name={isFullscreen ? "cdt-studio-collapse-graph" : "cdt-studio-expand-graph"}
            ariaLabel={t(isFullscreen ? "hierarchy.collapseGraph" : "hierarchy.expandGraph")}
            ariaExpanded={isFullscreen}
            title={t(isFullscreen ? "hierarchy.collapseGraph" : "hierarchy.expandGraph")}
            onClick={() => setIsFullscreen((current) => !current)}
          >
            {t(isFullscreen ? "hierarchy.collapseGraph" : "hierarchy.expandGraph")}
          </Button>
        </ZoomBar>
        <FallbackList $isGraphReady={isGraphReady}>
          <ScaledTree $zoom={zoom} data-testid="cdt-studio-tree-scale" data-zoom={zoom.toFixed(2)}>
            <Flex
              gap="4px"
              padding="0 0 6px 0"
              style={{ flexWrap: "wrap" }}
              data-testid="cdt-studio-tree-level-headers"
            >
              {spine.map((level, index) => (
                <Flex
                  key={level.attr}
                  align="center"
                  gap="2px"
                  padding="1px 6px"
                  border={`1px solid ${STUDIO_THEME.border}`}
                  borderRadius="6px"
                  background={STUDIO_THEME.surface}
                  data-testid={`cdt-studio-tree-column-${index}`}
                  data-attr={level.attr}
                >
                  {!readOnly && index > 0 && (
                    <InlineAction
                      as="span"
                      role="button"
                      aria-label={t("hierarchy.moveLevelUp", { label: columnNames[index] })}
                      onClick={() => onMoveLevel(index, index - 1)}
                    >
                      ◀
                    </InlineAction>
                  )}
                  <Text variant="small2" color={STUDIO_THEME.textSecondary} bold>
                    {columnNames[index]}
                  </Text>
                  {!readOnly && index < spine.length - 1 && (
                    <InlineAction
                      as="span"
                      role="button"
                      aria-label={t("hierarchy.moveLevelDown", { label: columnNames[index] })}
                      onClick={() => onMoveLevel(index, index + 1)}
                    >
                      ▶
                    </InlineAction>
                  )}
                </Flex>
              ))}
            </Flex>
            {tree.rootIds.map((rootId) => renderFallbackNode(rootId))}
          </ScaledTree>
        </FallbackList>
      </CanvasRoot>
    </TreeWorkspace>
  );
};
